import { datadogLogs, StatusType } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { Component, type PropsWithChildren } from 'react';

import { DATADOG_BUSINESS_UNIT } from '../configs/instrumentation.config';
import type {
  TelemetryLog,
  TelemetryLogConfig,
  TelemetryLogSeverityLevel,
  TelemetrySessionConfig,
} from '../models/telemetry.model';
import TelemetryProviderContext from './TelemetryProviderContext';

interface TelemetryProviderProps {
  applicationName: string;
  logTrackingConfig: TelemetryLogConfig;
  sessionTackingConfig: TelemetrySessionConfig;
  withLogTracking?: boolean;
  withSessionTracking?: boolean;
  /**
   * Optional callback to execute after provider mounts, regardless of initialization success
   */
  after?: () => void;
}

interface TelemetryProviderState {
  initialized: boolean;
}

class TelemetryProvider extends Component<
  PropsWithChildren<TelemetryProviderProps>,
  TelemetryProviderState
> {
  state: TelemetryProviderState = {
    initialized: false,
  };
  componentDidMount() {
    const { initialized } = this.state;
    if (!initialized) {
      this.initialize();
      this.setState({ initialized: true });
    }

    if (this.props.after) {
      this.props.after();
    }
  }

  initialize() {
    if (this.props.withSessionTracking) {
      datadogRum.init(this.props.sessionTackingConfig);
      datadogRum.startSessionReplayRecording();
    }
    if (this.props.withLogTracking) {
      datadogLogs.init(this.props.logTrackingConfig);
      console.log = this.trackLogEvent(StatusType.info);
      console.error = this.trackLogEvent(StatusType.error);
      console.debug = this.trackLogEvent(StatusType.debug);
      console.warn = this.trackLogEvent(StatusType.warn);
    }
    this.addContext('business-unit', DATADOG_BUSINESS_UNIT);
    this.addContext('app', { name: this.props.applicationName });
  }

  addContext(name: string, value: unknown) {
    datadogLogs.setGlobalContextProperty(name, value);
    datadogRum.setGlobalContextProperty(name, value);
  }

  log({ message, logLevel = StatusType.info, args = [] }: TelemetryLog) {
    //Log to Data Dog
    // Convert args array to context object for proper Datadog API usage
    const context: Record<string, unknown> = {};

    if (args.length > 0) {
      // Serialize each argument properly, especially Error objects
      args.forEach((arg, index) => {
        if (arg instanceof Error) {
          // Extract full error details including stack trace
          context[`error_${index}`] = {
            message: arg.message,
            name: arg.name,
            stack: arg.stack,
          };
        } else if (typeof arg === 'object' && arg !== null) {
          // Keep objects as-is for Datadog to display
          context[`arg_${index}`] = arg;
        } else {
          // For primitives (string, number, boolean)
          context[`arg_${index}`] = arg;
        }
      });
    }

    datadogLogs.logger.log(message, context, logLevel);
  }

  trackLogEvent(
    logLevel: TelemetryLogSeverityLevel
  ): (message: unknown, ...args: Record<string, unknown>[]) => void {
    return (message: unknown, ...args: Record<string, unknown>[]) => {
      const traceMessage =
        typeof message === 'string'
          ? (message as string)
          : `${this.props.applicationName} ${logLevel.toUpperCase()} Event`;
      this.log({
        message: traceMessage,
        logLevel,
        args,
      });
    };
  }

  render() {
    const { children } = this.props;
    return (
      <TelemetryProviderContext.Provider
        value={{ addTelemetryContext: this.addContext, log: this.log }}
      >
        {children}
      </TelemetryProviderContext.Provider>
    );
  }
}

export default TelemetryProvider;
